import { useMutation } from "@tanstack/react-query";
import {
  Control,
  ControlCodes,
  getControlName,
  service,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  CopyButton,
  HelpIcon,
  Input,
  InputField,
  LinkIcon,
  RefreshIcon,
  notifyError,
  notifySuccess,
} from "@theprelude/ds";
import { z } from "zod";
import { IntegrationForm } from "../integration-form";

const controlCode = ControlCodes.SENTINELONE;
const controlName = getControlName(controlCode);

export const SentinelOneIntegrationForm = ({
  instance,
}: {
  instance?: Control;
}) => (
  <IntegrationForm
    control={controlCode}
    instanceId={instance?.instance_id}
    instanceName={instance?.name ?? undefined}
    description={`Connect ${controlName} to begin assessment of control deployment, health and configuration.`}
    fields={{
      baseUrl: {
        label: "Base URL",
        type: "url",
        placeholder: "Enter your Base URL",
        defaultValue: instance?.api,
        required: true,
        validator: z.string().url(),
      },
      siteId: {
        label: "Account ID",
        placeholder: "Enter your Account ID",
        defaultValue: instance?.username,
        required: true,
        validator: z.string().min(1),
      },
      apiToken: {
        label: "API Token",
        type: "password",
        placeholder: "Enter your API Token",
        required: true,
        validator: z.string().min(1),
      },
    }}
    transformParams={({ baseUrl, siteId, apiToken }) => ({
      api: baseUrl,
      user: siteId,
      secret: apiToken,
    })}
  >
    {instance && <GenerateWebhookForm />}
  </IntegrationForm>
);

const GenerateWebhookForm = () => {
  const authConfig = useAuthConfig();

  const generateWebhook = useMutation({
    mutationFn: async () =>
      service(authConfig).partner.webhookGenerate(
        controlCode,
      ) as unknown as Promise<{
        url: string;
        secret: string;
        headers: Record<string, string>;
      }>,
    onSuccess: async () => {
      notifySuccess({ message: "Webhook generated" });
    },
    onError: (error) => {
      notifyError({ message: "Failed to generate webhook", error });
    },
  });

  return (
    <>
      <div className="flex items-center gap-2 border-y border-y-stroke-line py-4 text-secondary-foreground">
        <LinkIcon className="h-5 w-4 flex-shrink-0" />
        Alert Management Webhook
      </div>

      <div className="flex flex-col gap-4">
        <Button
          loading={generateWebhook.isPending}
          onClick={() => generateWebhook.mutate()}
          disabled={generateWebhook.isPending}
          intent="primary"
          icon={<RefreshIcon />}
        >
          Generate Webhook Credentials
        </Button>

        {generateWebhook.data && (
          <>
            <InputField label="Base URL" required>
              <Input
                readOnly
                after={<CopyButton toCopy={generateWebhook.data.url} />}
                value={generateWebhook.data.url}
              />
            </InputField>

            <InputField label="App Secret" required>
              <Input
                after={<CopyButton toCopy={generateWebhook.data.secret} />}
                readOnly
                value={generateWebhook.data.secret}
              />
            </InputField>

            <InputField label="Headers">
              <div className="flex items-center gap-1 rounded-md border border-stroke-input bg-secondary-background p-2 text-sm shadow-md">
                <pre>
                  <code>
                    {JSON.stringify(generateWebhook.data.headers, null, 2)}
                  </code>
                </pre>
                <CopyButton
                  toCopy={JSON.stringify(generateWebhook.data.headers)}
                />
              </div>
            </InputField>
          </>
        )}

        <a
          href="https://support.preludesecurity.com/docs/sentinel-one"
          target="_blank"
          className="mb-2 flex cursor-pointer flex-row items-center justify-start gap-2 text-sm text-primary-foreground underline underline-offset-2 font-normal"
          rel="noreferrer"
        >
          <HelpIcon className="text-secondary-foreground" />{" "}
          <span>How do I setup my webhook in {controlName}?</span>
        </a>
      </div>
    </>
  );
};
